import type { QuikQStation, QuikQStationsParams } from './quikqApi';
import { getCachedStations, setCachedStations } from './stationsCache';

const CACHE_SCOPE = 'fuel_prices';
const IN_MEMORY_TTL_MS = 60 * 1000;

function getApiBaseUrl(): string {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }
  // Relative /api: CRA dev proxy (package.json → localhost:5000) and production
  // same-origin when Express serves the build.
  return '/api';
}

function buildStationsUrl(params: QuikQStationsParams): string {
  const search = new URLSearchParams();
  if (params.latitude != null) search.set('latitude', String(params.latitude));
  if (params.longitude != null) search.set('longitude', String(params.longitude));
  const radius = params.radius ?? params.miles;
  if (radius != null) search.set('radius', String(radius));
  if (params.stationCode) search.set('stationCode', params.stationCode);
  if (params.city) search.set('city', params.city);
  if (params.state) search.set('state', params.state);
  if (params.zipCode) search.set('zipCode', params.zipCode);

  const base = getApiBaseUrl();
  const path = `${base}/fuel-prices/stations?${search.toString()}`;
  if (base.startsWith('http')) {
    return path;
  }
  if (typeof window !== 'undefined') {
    return `${window.location.origin}${path}`;
  }
  return path;
}

function buildCacheKey(params: QuikQStationsParams): string {
  const roundedLat = typeof params.latitude === 'number' ? params.latitude.toFixed(2) : '';
  const roundedLng = typeof params.longitude === 'number' ? params.longitude.toFixed(2) : '';
  const radius = params.radius ?? params.miles ?? '';
  return [
    CACHE_SCOPE,
    params.stationCode || '*ANY',
    radius,
    roundedLat,
    roundedLng,
    params.city || '',
    params.state || '',
    params.zipCode || '',
  ].join('|');
}

class FuelPricesApiService {
  private stationsCache = new Map<string, { ts: number; data: QuikQStation[] }>();
  private stationsInflight = new Map<string, Promise<QuikQStation[]>>();
  private bypassPersistentUntil = 0;

  /** Drop in-memory cache and skip IndexedDB reads for a short window. */
  invalidateCaches(bypassMs = 60_000): void {
    this.stationsCache.clear();
    this.bypassPersistentUntil = Date.now() + bypassMs;
  }

  async getStations(
    params: QuikQStationsParams,
    options?: { signal?: AbortSignal }
  ): Promise<QuikQStation[]> {
    const cacheKey = buildCacheKey(params);
    const cached = this.stationsCache.get(cacheKey);
    if (cached && Date.now() - cached.ts < IN_MEMORY_TTL_MS) {
      return cached.data;
    }

    const bypassPersistent =
      params._bypassPersistentCache === true || Date.now() < this.bypassPersistentUntil;
    if (!bypassPersistent) {
      try {
        const persisted = await getCachedStations(cacheKey);
        if (persisted) {
          this.stationsCache.set(cacheKey, { ts: persisted.cachedAt, data: persisted.data });
          if (persisted.isFresh) {
            return persisted.data;
          }
        }
      } catch (error) {
        console.warn('fuelPricesApi: cache lookup failed', error);
      }
    }

    const inflight = this.stationsInflight.get(cacheKey);
    if (inflight) {
      return inflight;
    }

    const requestPromise = (async (): Promise<QuikQStation[]> => {
      const url = buildStationsUrl(params);

      const response = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        signal: options?.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to load fuel_prices stations: ${response.status}`);
      }

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error(
          'fuel_prices API returned HTML instead of JSON. Restart the backend server (npm run serve:api) so /api/fuel-prices/stations is available.'
        );
      }

      const data = await response.json();
      if (process.env.NODE_ENV === 'production') {
        console.info(
          `[NSF] Stations loaded from ${data?.source || 'fuel_prices'} API (${data?.count ?? '?'} stations)`
        );
      }
      const stations: QuikQStation[] = Array.isArray(data?.stations) ? data.stations : [];

      this.stationsCache.set(cacheKey, { ts: Date.now(), data: stations });
      if (!bypassPersistent && stations.length > 0) {
        void setCachedStations(cacheKey, stations, CACHE_SCOPE);
      }

      return stations;
    })();

    this.stationsInflight.set(cacheKey, requestPromise);
    try {
      return await requestPromise;
    } finally {
      if (this.stationsInflight.get(cacheKey) === requestPromise) {
        this.stationsInflight.delete(cacheKey);
      }
    }
  }
  async searchStationsByName(
    name: string,
    options?: {
      latitude?: number;
      longitude?: number;
      radius?: number;
      limit?: number;
      signal?: AbortSignal;
    }
  ): Promise<QuikQStation[]> {
    const trimmed = name.trim();
    if (trimmed.length < 2) return [];

    const search = new URLSearchParams();
    search.set('stationName', trimmed);
    search.set('limit', String(options?.limit ?? 8));
    if (options?.latitude != null) search.set('latitude', String(options.latitude));
    if (options?.longitude != null) search.set('longitude', String(options.longitude));
    if (options?.radius != null) search.set('radius', String(options.radius));

    const base = getApiBaseUrl();
    const path = `${base}/fuel-prices/stations?${search.toString()}`;
    const url = base.startsWith('http')
      ? path
      : typeof window !== 'undefined'
        ? `${window.location.origin}${path}`
        : path;

    const response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      signal: options?.signal,
    });
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data?.stations) ? data.stations : [];
  }
}

export const fuelPricesApiService = new FuelPricesApiService();
